const VALID_PROD_DOMAINS = [
  "us.fullscript.com",
  "ca.fullscript.com",
  "healthwavehq.com",
  "www.healthwavehq.com",
  "healthwavehq.ca",
  "www.healthwavehq.ca",
  "emersonecologics.com",
  "www.emersonecologics.com",
  "login.emersonecologics.com",
];

const EE_STAGING_DOMAIN = "staging.emersonecologics.com";

const isNodeEnv = (env: string) => {
  return process.env.NODE_ENV === env;
};

/**
 * Used for checking in what mode the fs-node server is running
 * - development
 * - staging
 * - production
 */
const isTargetEnv = (env: string) => {
  return process.env.TARGET_ENV === env;
};

/**
 * Verifies if we are running on the server side.
 * Returns `true` if we are running on the server side, `false` otherwise.
 */
const isSSR = () => typeof window === "undefined";

/**
 * Verifies if we are running in a E2E environment.
 * Returns `true` if we are, `false` otherwise.
 */
const isE2E = () => {
  return process.env.E2E_ENV === "true";
};

const isMasterBranch = () => {
  return process.env.CI_COMMIT_REF_NAME === "master";
};

const isProdDomain = () => {
  return !isSSR() && VALID_PROD_DOMAINS.includes(window.location.hostname);
};

const isSandboxDomain = () => {
  return (
    !isSSR() &&
    (window.location.hostname === "us-snd.fullscript.io" ||
      window.location.hostname === "ca-snd.fullscript.io")
  );
};

const isEEStagingDomain = () => {
  return !isSSR() && window.location.hostname === EE_STAGING_DOMAIN;
};

/**
 * Verifies if the current environment is a "production" JS bundle.
 * This will evaluate to `true` for review envs, staging, sandbox and production.
 *
 * See {@link isProduction} if you want to specifically target `us.fullscript.com` or `ca.fullscript.com`
 */
const isProductionBundle = () => {
  return isNodeEnv("production");
};

/**
 * Verifies if the current environment is `us.fullscript.com` or `ca.fullscript.com`.
 * This will only evaluate to `true` for the main production websites.
 *
 * See {@link isProductionBundle} if you want to target review envs, staging, sandbox and production
 */
const isProduction = () => {
  return isTargetEnv("production") || (isProductionBundle() && isMasterBranch() && isProdDomain());
};

/**
 * Verifies if the current environment is `us-snd.fullscript.io` or `ca-snd.fullscript.io`.
 * This will only evaluate to `true` for the sandbox websites.
 */
const isSandbox = () => {
  return isProductionBundle() && isMasterBranch() && isSandboxDomain();
};

/**
 * Verifies if the current environment is a `review env`, or `staging.r.fullscript.io`.
 * This will only evaluate to `true` for staging itself and review envs.
 *
 * See {@link isProductionBundle} if you want to target review envs, staging, sandbox and production
 */
const isStaging = () => {
  return isTargetEnv("staging") || (isProductionBundle() && !isProduction() && !isSandbox());
};

/**
 * Verifies if the current environment is a review env from an MR (merge request).
 * This will only evaluate to `true` for these environments.
 */
const isReview = () => {
  return isStaging() && !isMasterBranch() && !isE2E();
};

/**
 * Verifies if the current environment is `staging.emersonecologics.com`.
 * This will only evaluate to `true` for that environment.
 */
const isEEStaging = () => {
  return isStaging() && isEEStagingDomain();
};

/**
 * Verifies if the current env is a dev environment.
 */
const isDevelopment = () => {
  return isNodeEnv("development") || isTargetEnv("development");
};

/**
 * Verifies if the current env is a test environment.
 * I.E. when running jest or vitest.
 */
const isTest = () => {
  return isNodeEnv("test");
};

type PossibleEnvs =
  | "production"
  | "sandbox"
  | "staging"
  | "development"
  | "test"
  | "review"
  | "e2e";

/**
 * Returns the current environment as a string, e.g. 'production', 'sandbox', 'staging', 'development', 'test', 'review'
 *
 * @returns {PossibleEnvs} the current running environment
 */
const currentEnv = (): PossibleEnvs => {
  if (isProduction()) {
    return "production";
  } else if (isReview()) {
    return "review";
  } else if (isE2E()) {
    return "e2e";
  } else if (isStaging()) {
    return "staging";
  } else if (isDevelopment()) {
    return "development";
  } else if (isSandbox()) {
    return "sandbox";
  }

  return "test";
};

const isRemix = () => {
  return !!process.env.IS_REMIX;
};

export {
  currentEnv,
  isDevelopment,
  isE2E,
  isEEStaging,
  isProduction,
  isProductionBundle,
  isRemix,
  isReview,
  isSandbox,
  isSSR,
  isStaging,
  isTest,
  isMasterBranch,
};
